import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Chip,
  Collapse,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { useNotification } from "../../providers/NotificationProvider";
import { fetchWorkerDetail, fetchWorksByWorker } from "../../api/memberApi";

const WORK_TYPE_COLORS = {
  ToDo: "default",
  Doing: "primary",
  Done: "success",
};

export default function WorkHistoryPage() {
  const intl = useIntl();
  const navigate = useNavigate();
  const { workerId } = useParams();
  const notify = useNotification();

  const [worker, setWorker] = useState(null);
  const [works, setWorks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedWorkId, setExpandedWorkId] = useState(null);

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString();
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [workerData, worksData] = await Promise.all([
        fetchWorkerDetail(workerId).catch(() => null),
        fetchWorksByWorker(workerId).catch(() => []),
      ]);
      setWorker(workerData);
      setWorks(Array.isArray(worksData) ? worksData : []);
    } catch (err) {
      notify.apiError(err);
    } finally {
      setLoading(false);
    }
  }, [workerId, notify]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* ヘッダー */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/workers/${workerId}`, { state: { released: true } })}
          variant="text"
        >
          {intl.formatMessage({ id: "common.back" })}
        </Button>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {intl.formatMessage({ id: "worker.workHistory.title" })}
          </Typography>
          {worker?.workerName && (
            <Typography variant="body2" color="text.secondary">
              {worker.workerName}
            </Typography>
          )}
        </Box>
        <Chip
          label={intl.formatMessage({ id: "worker.released" })}
          color="default"
          size="small"
        />
      </Box>

      {works.length === 0 ? (
        <Typography color="text.secondary">
          {intl.formatMessage({ id: "worker.workHistory.noWorks" })}
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "grey.50" }}>
                <TableCell sx={{ fontWeight: 600 }}>
                  {intl.formatMessage({ id: "work.name" })}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {intl.formatMessage({ id: "work.status" })}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {intl.formatMessage({ id: "work.startDate" })}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {intl.formatMessage({ id: "work.endDate" })}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {works.map((work) => (
                <React.Fragment key={work.workId}>
                  <TableRow
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() =>
                      setExpandedWorkId(
                        expandedWorkId === work.workId ? null : work.workId
                      )
                    }
                  >
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        {expandedWorkId === work.workId
                          ? <ExpandLessIcon fontSize="small" color="action" />
                          : <ExpandMoreIcon fontSize="small" color="action" />
                        }
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {work.workName}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={work.workType || "—"}
                        color={WORK_TYPE_COLORS[work.workType] || "default"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(work.startDate)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(work.endDate)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  {/* 作業内容の展開表示 */}
                  {expandedWorkId === work.workId && work.workDescription && (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ py: 0 }}>
                        <Collapse in timeout="auto" unmountOnExit>
                          <Box sx={{ p: 2, backgroundColor: "grey.50", borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                              {intl.formatMessage({ id: "work.description" })}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}
                            >
                              {work.workDescription}
                            </Typography>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
